import { GlobalIcon, UndoIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { useInboxEmbed } from "../context/useInboxEmbed";

interface TranslateComposerButtonProps {
  canTranslate: boolean;
  canUndo: boolean;
  isTranslating: boolean;
  disabled?: boolean;
  onTranslate: () => void;
  onUndo: () => void;
  iconSize?: IconButtonSize;
  ghost?: boolean;
}

const TRANSLATE_LABEL = "Translate to Spanish";

const GhostIconButton = ({
  icon,
  label,
  onClick,
  disabled,
  loading,
  tooltipZIndex
}: {
  icon: IconSvgElement;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  tooltipZIndex?: number;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={disabled || loading}
        className="shrink-0 rounded-lg p-1 text-content-1000 transition-colors hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {loading ? (
          <span className="block size-5 animate-spin rounded-full border-2 border-alpha-200 border-t-primary-500" />
        ) : (
          <HugeiconsIcon icon={icon} size={20} />
        )}
      </button>
    </TooltipTrigger>
    <TooltipContent
      className="z-50 rounded-lg px-3 py-2 text-xs font-medium text-white"
      style={tooltipZIndex ? { zIndex: tooltipZIndex } : undefined}
    >
      {label}
    </TooltipContent>
  </Tooltip>
);

export const TranslateComposerButton = ({
  canTranslate,
  canUndo,
  isTranslating,
  disabled,
  onTranslate,
  onUndo,
  iconSize = IconButtonSize.SM,
  ghost = false
}: TranslateComposerButtonProps) => {
  const embed = useInboxEmbed();

  if (embed?.compact && ghost)
    return (
      <div className="flex items-center gap-2">
        <Show if={canUndo}>
          <GhostIconButton
            icon={UndoIcon}
            label="Undo"
            onClick={onUndo}
            disabled={isTranslating}
            tooltipZIndex={embed.modalZIndex}
          />
        </Show>
        <Show if={canTranslate}>
          <GhostIconButton
            icon={GlobalIcon}
            label={TRANSLATE_LABEL}
            onClick={onTranslate}
            disabled={disabled}
            loading={isTranslating}
            tooltipZIndex={embed.modalZIndex}
          />
        </Show>
      </div>
    );

  if (embed?.compact)
    return (
      <div className="flex items-center gap-3">
        <Show if={canUndo}>
          <IconButton
            HugeIcon={UndoIcon}
            variant={IconButtonVariant.BASE}
            size={iconSize}
            onClick={onUndo}
            disabled={isTranslating}
            tooltip="Undo"
            tooltipZIndex={embed.modalZIndex}
            aria-label="Undo"
          />
        </Show>
        <Show if={canTranslate}>
          <IconButton
            HugeIcon={GlobalIcon}
            variant={IconButtonVariant.BASE}
            size={iconSize}
            onClick={onTranslate}
            loading={isTranslating}
            disabled={disabled || isTranslating}
            tooltip={TRANSLATE_LABEL}
            tooltipZIndex={embed.modalZIndex}
            aria-label={TRANSLATE_LABEL}
          />
        </Show>
      </div>
    );

  return (
    <div className="flex items-center gap-3">
      <Show if={canUndo}>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={onUndo}
          disabled={isTranslating}
        >
          Undo
        </Button>
      </Show>
      <Show if={canTranslate}>
        <Button
          HugeIcon={GlobalIcon}
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={onTranslate}
          loading={isTranslating}
          loadingMode="keep-label"
          disabled={disabled || isTranslating}
        >
          {TRANSLATE_LABEL}
        </Button>
      </Show>
    </div>
  );
};
